import { useState, useEffect } from "react";
import { useNavigate, Outlet, useParams, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { getDotPatternStyle } from "@/components/AppBackground";
import { AppMainHeader } from "@/components/AppMainHeader";
import { MobileBackBar } from "@/components/MobileBackBar";
import { useAuth } from "@/contexts/AuthContext";
import { useSettings } from "@/contexts/SettingsContext";
import { createMetricClient, getMetricClients, updateMetricClient, deleteMetricClient } from "@/lib/api";
import { Loader2, Plus, MoreVertical } from "lucide-react";
import { toast } from "sonner";

export default function MetricasLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { clientId } = useParams();
  const { user, authLoading } = useAuth();
  const settings = useSettings();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openAdd, setOpenAdd] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [defaultPlatform, setDefaultPlatform] = useState("instagram");

  const [openEdit, setOpenEdit] = useState(false);
  const [editClientId, setEditClientId] = useState("");
  const [editName, setEditName] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/login", { replace: true });
      return;
    }
    if (user.permissions?.metricas === false) {
      navigate("/", { replace: true });
    }
  }, [authLoading, user, navigate]);

  const reloadClients = async () => {
    setLoading(true);
    try {
      const data = await getMetricClients();
      const list = data.clients || [];
      setClients(list);
      if (!clientId && list.length) navigate(`/metricas/${list[0].id}`, { replace: true });
    } catch (_) {
      toast.error("Error al cargar clientes");
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user || user.permissions?.metricas === false) return;
    reloadClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, location.pathname]);

  const handleCreateClient = async () => {
    const name = newName.trim();
    if (!name) return toast.error("Indica el nombre del cliente");
    setCreating(true);
    try {
      const created = await createMetricClient(name);
      toast.success("Cliente creado");
      setOpenAdd(false);
      setNewName("");
      await reloadClients();
      navigate(`/metricas/${created.id}?platform=${defaultPlatform}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo crear el cliente");
    } finally {
      setCreating(false);
    }
  };

  const handleSaveEdit = async () => {
    const name = editName.trim();
    if (!name) return toast.error("Indica el nombre del cliente");
    if (!editClientId) return;
    setSavingEdit(true);
    try {
      await updateMetricClient(editClientId, name);
      toast.success("Cliente actualizado");
      setOpenEdit(false);
      setEditClientId("");
      setEditName("");
      await reloadClients();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo actualizar el cliente");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget?.id) return;
    setDeleting(true);
    try {
      await deleteMetricClient(deleteTarget.id);
      toast.success("Cliente eliminado");
      const removedId = deleteTarget.id;
      setDeleteTarget(null);
      if (clientId === removedId) {
        navigate("/metricas", { replace: true });
      }
      await reloadClients();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo eliminar el cliente");
    } finally {
      setDeleting(false);
    }
  };

  if (authLoading || !user || user.permissions?.metricas === false) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" strokeWidth={1.5} />
      </div>
    );
  }

  const titleSuffix =
    clientId && clients?.length ? ` / ${clients.find((c) => c.id === clientId)?.name || ""}` : "";

  return (
    <div className="h-dvh flex flex-col bg-background overflow-hidden">
      <AppMainHeader title="Métricas" titleSuffix={titleSuffix} />
      <div className="flex flex-1 min-h-0 overflow-hidden">
        <aside className={`${clientId ? "hidden md:flex" : "flex"} w-full md:w-80 shrink-0 flex-col md:border-r border-gray-300 bg-background overflow-hidden`}>
          <div className="p-4 shrink-0 border-b border-gray-200">
            <Button onClick={() => setOpenAdd(true)} className="gap-2 w-full">
              <Plus className="w-4 h-4" /> Añadir cliente
            </Button>
          </div>
          <div className="p-4 flex-1 overflow-auto mobile-nav-scroll">
            {loading ? (
              <div className="flex items-center justify-center py-10 text-muted-foreground">
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                Cargando clientes...
              </div>
            ) : clients.length === 0 ? (
              <Card className="shadow-none">
                <CardHeader>
                  <CardTitle className="text-base">No hay clientes</CardTitle>
                  <CardDescription>Crea el primer cliente para empezar a analizar métricas.</CardDescription>
                </CardHeader>
              </Card>
            ) : (
              <ul className="space-y-2">
                {clients.map((client) => (
                  <li key={client.id}>
                    <Card className="shadow-none hover:bg-muted/50">
                      <CardContent className="p-3 flex gap-2 items-center">
                        <button
                          type="button"
                          className="flex-1 min-w-0 text-left rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          onClick={() => navigate(`/metricas/${client.id}`)}
                        >
                          <p className="text-sm font-medium truncate">{client.name}</p>
                          <p className="text-xs text-muted-foreground truncate">{client.id}</p>
                        </button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="shrink-0 h-8 w-8"
                              aria-label="Acciones del cliente"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onSelect={(e) => {
                                e.preventDefault();
                                setEditClientId(client.id);
                                setEditName(client.name || "");
                                setOpenEdit(true);
                              }}
                            >
                              Editar nombre
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-destructive focus:text-destructive"
                              onSelect={(e) => {
                                e.preventDefault();
                                setDeleteTarget({ id: client.id, name: client.name || client.id });
                              }}
                            >
                              Eliminar
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </CardContent>
                    </Card>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </aside>
        <div className={`${clientId ? "" : "hidden md:block"} flex-1 min-w-0 overflow-y-auto relative mobile-nav-scroll`} style={getDotPatternStyle(settings)}>
          {clientId && <MobileBackBar to="/metricas" label="Clientes" />}
          <Outlet context={{ clients, reloadClients }} />
        </div>
      </div>

      <Dialog open={openAdd} onOpenChange={setOpenAdd}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Añadir cliente</DialogTitle>
            <DialogDescription>Indica el nombre y la plataforma por defecto para las cuentas de este cliente.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor="metric-client-name">Nombre del cliente</Label>
              <Input id="metric-client-name" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Ej. Restaurante Luna" />
            </div>
            <div className="space-y-2">
              <Label>Plataforma por defecto</Label>
              <Select value={defaultPlatform} onValueChange={setDefaultPlatform}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="instagram">Instagram</SelectItem>
                  <SelectItem value="tiktok">TikTok</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpenAdd(false)}>Cancelar</Button>
            <Button onClick={handleCreateClient} disabled={creating}>
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Crear cliente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={openEdit} onOpenChange={(o) => { setOpenEdit(o); if (!o) { setEditClientId(""); setEditName(""); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar cliente</DialogTitle>
            <DialogDescription>Cambia el nombre visible del cliente en la lista.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="metric-client-edit-name">Nombre</Label>
            <Input
              id="metric-client-edit-name"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              placeholder="Nombre del cliente"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpenEdit(false)}>Cancelar</Button>
            <Button onClick={handleSaveEdit} disabled={savingEdit}>
              {savingEdit ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar cliente?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminarán también las cuentas de redes y el histórico de métricas asociados a «{deleteTarget?.name}». Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancelar</AlertDialogCancel>
            <Button variant="destructive" onClick={handleConfirmDelete} disabled={deleting}>
              {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Eliminar
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
